import { convex } from "@/lib/convexClient";
import { api } from "../../../convex/_generated/api";

export interface S3Credentials {
  accessKeyId: string;
  secretAccessKey: string;
  endpoint: string;
  region: string;
  bucketName: string;
  useCorsProxy?: boolean;
}

export interface EncryptedS3Credentials {
  encryptedAccessKeyId: string;
  encryptedSecretAccessKey: string;
}

export class EncryptedS3CredentialService {
  /**
   * Store S3 credentials encrypted in the database
   */
  static async storeCredentials(craftspaceId: string, credentials: S3Credentials): Promise<void> {
    try {
      // Encrypt the credentials using the Convex action
      const encryptedAccessKeyId = await EncryptedS3CredentialService.encryptData(
        credentials.accessKeyId,
        craftspaceId,
      );
      const encryptedSecretAccessKey = await EncryptedS3CredentialService.encryptData(
        credentials.secretAccessKey,
        craftspaceId,
      );

      // Store encrypted credentials in the database
      await convex.mutation(api.s3Connections.upsert, {
        craftspace_id: craftspaceId,
        name: credentials.bucketName,
        provider: "custom-s3",
        bucket_name: credentials.bucketName,
        region: credentials.region,
        endpoint: credentials.endpoint,
        encrypted_access_key_id: encryptedAccessKeyId,
        encrypted_secret_access_key: encryptedSecretAccessKey,
      });
    } catch (error) {
      console.error("Error storing encrypted S3 credentials:", error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt S3 credentials from the database
   */
  static async getCredentials(craftspaceId: string): Promise<S3Credentials | null> {
    try {
      // Get encrypted credentials from database
      const connection = await convex.query(api.s3Connections.getByCraftspace, { craftspaceId });
      if (!connection) {
        return null;
      }

      if (!connection.encrypted_access_key_id || !connection.encrypted_secret_access_key) {
        return null;
      }

      // Decrypt the credentials
      const accessKeyId = await EncryptedS3CredentialService.decryptData(
        connection.encrypted_access_key_id,
        craftspaceId,
      );
      const secretAccessKey = await EncryptedS3CredentialService.decryptData(
        connection.encrypted_secret_access_key,
        craftspaceId,
      );

      return {
        accessKeyId,
        secretAccessKey,
        endpoint: connection.endpoint,
        region: connection.region,
        bucketName: connection.bucket_name,
      };
    } catch (error) {
      console.error("Error retrieving encrypted S3 credentials:", error);
      return null;
    }
  }

  /**
   * Update existing S3 credentials
   */
  static async updateCredentials(craftspaceId: string, credentials: S3Credentials): Promise<void> {
    try {
      // Encrypt the new credentials
      const encryptedAccessKeyId = await EncryptedS3CredentialService.encryptData(
        credentials.accessKeyId,
        craftspaceId,
      );
      const encryptedSecretAccessKey = await EncryptedS3CredentialService.encryptData(
        credentials.secretAccessKey,
        craftspaceId,
      );

      // Update the encrypted credentials in the database
      await convex.mutation(api.s3Connections.upsert, {
        craftspace_id: craftspaceId,
        name: credentials.bucketName,
        provider: "custom-s3",
        bucket_name: credentials.bucketName,
        region: credentials.region,
        endpoint: credentials.endpoint,
        encrypted_access_key_id: encryptedAccessKeyId,
        encrypted_secret_access_key: encryptedSecretAccessKey,
      });
    } catch (error) {
      console.error("Error updating encrypted S3 credentials:", error);
      throw error;
    }
  }

  /**
   * Remove encrypted credentials from the database
   */
  static async removeCredentials(craftspaceId: string): Promise<void> {
    try {
      await convex.mutation(api.s3Connections.remove, { craftspaceId });
    } catch (error) {
      console.error("Error removing encrypted S3 credentials:", error);
      throw error;
    }
  }

  /**
   * Test if credentials exist and are valid
   */
  static async hasValidCredentials(craftspaceId: string): Promise<boolean> {
    try {
      const connection = await convex.query(api.s3Connections.getByCraftspace, { craftspaceId });
      if (!connection) {
        return false;
      }

      return !!(connection.encrypted_access_key_id && connection.encrypted_secret_access_key);
    } catch (error) {
      console.error("Error checking credentials validity:", error);
      return false;
    }
  }

  /**
   * Encrypt data using Convex action
   */
  private static async encryptData(data: string, craftspaceId: string): Promise<string> {
    const encrypted = await convex.action(api.actions.s3CredentialsCrypto.transform, {
      craftspaceId,
      mode: "encrypt",
      data,
    });
    return encrypted;
  }

  /**
   * Decrypt data using Convex action
   */
  private static async decryptData(encryptedData: string, craftspaceId: string): Promise<string> {
    const decrypted = await convex.action(api.actions.s3CredentialsCrypto.transform, {
      craftspaceId,
      mode: "decrypt",
      data: encryptedData,
    });
    return decrypted;
  }
}
